import { flexRender, Row } from '@tanstack/react-table';
import type { OrderForWorklist } from '@/Models/GenericWorklist/WorklistResult';
import React, { ReactElement } from 'react';
import { NavigateFunction } from 'react-router';
import Styles from './WorklistDataGrid.module.scss';
import { IWorklistStore } from '@/Pages/GenericWorklist/Stores/WorklistStore';
import { IWorklistSelectionHelper } from '@/Pages/GenericWorklist/Stores/WorklistSelectionHelper';

interface RenderRowProps {
    readonly row: Row<OrderForWorklist>;
    readonly navigateFunction: NavigateFunction;
    readonly worklistStore: IWorklistStore;
    readonly worklistSelectionHelper: IWorklistSelectionHelper;
}

/**
 * Memoized with a comparator on `row.original` identity.
 *
 * `row.original` is the same object reference between selection-only changes because
 * `WorklistStore.setSelection` spreads the existing state, keeping the `results.Orders`
 * array intact. This means only rows whose underlying `OrderForWorklist` data actually
 * changed (i.e. after a full refetch) will re-render. Column visibility toggles are
 * handled by the caller, which includes the visible-columns key in the React `key` so
 * the row is rebuilt when the table layout changes without order data changes.
 * Selection-state rendering is handled independently by the `observer`-wrapped
 * `SelectionCell`.
 */
export const WorklistDataGridRow = React.memo(
    function WorklistDataGridRow({ row, navigateFunction, worklistStore, worklistSelectionHelper }: RenderRowProps): ReactElement {
        const handleSelectionClick = (event: React.MouseEvent<HTMLTableCellElement>) => {
            event.stopPropagation();
            worklistSelectionHelper.toggleSelection(row.original.OrderInfo.OrderID);
        };

        return (
            <tr
                key={row.id}
                onClick={() => navigateFunction('/Orders/Detail/' + row.original.OrderInfo.OrderID)}
                onContextMenu={() => worklistStore.setSelection([row.original.OrderInfo.OrderID])}
            >
                {row.getVisibleCells().map((cell) => {
                    const isRemarkColumn = cell.column.id === 'Remarks';
                    return (
                        <td
                            key={cell.id}
                            className={isRemarkColumn ? Styles.verticalCenterCell : ''}
                            onClick={cell.column.id === 'IsSelected' ? handleSelectionClick : undefined}
                        >
                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </td>
                    );
                })}
            </tr>
        );
    },
    (prev, next) =>
        prev.row.id === next.row.id
        && prev.row.original === next.row.original
        && prev.navigateFunction === next.navigateFunction
        && prev.worklistSelectionHelper === next.worklistSelectionHelper
        && prev.worklistStore === next.worklistStore,
);
